import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import EmptyState from "@/components/EmptyState";
import TradeAttachmentGallery from "@/components/TradeAttachmentGallery";
import TradeAttachmentsField from "@/components/TradeAttachmentsField";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Edit, Trash2, TrendingUp, TrendingDown, List } from "lucide-react";
import { API_URL } from "@/lib/api";
import { formatExecutionDateForDisplay, normalizeExecutionDate } from "@/lib/date";

const API = API_URL;

export default function TradesList({ currentStrategy, mode }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTrade, setEditingTrade] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    currency_pair: "",
    risk_amount: "",
    profit_loss: "",
    execution_date: "",
    notes: "",
    attachments: [],
    setup: "",
    tags: [],
    grade: "",
    emotion: "",
  });
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const loadTrades = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/trades?strategy_id=${currentStrategy.id}&mode=${mode}`,
        {
        headers: { Authorization: `Bearer ${token}` },
        }
      );
      const sortedTrades = response.data.sort((a, b) =>
        normalizeExecutionDate(b.execution_date).localeCompare(normalizeExecutionDate(a.execution_date))
      );
      setTrades(sortedTrades);
    } catch (error) {
      console.error("Error loading trades:", error);
    } finally {
      setLoading(false);
    }
  }, [currentStrategy.id, mode]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    setFormData({
      currency_pair: trade.currency_pair,
      risk_amount: trade.risk_amount.toString(),
      profit_loss: trade.profit_loss.toString(),
      execution_date: normalizeExecutionDate(trade.execution_date),
      notes: trade.notes,
      attachments: trade.attachments || [],
      setup: trade.setup || "",
      tags: trade.tags || [],
      grade: trade.grade ? `${trade.grade}` : "",
      emotion: trade.emotion ? `${trade.emotion}` : "",
    });
    setOpenDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const tradeData = {
        ...formData,
        strategy_id: currentStrategy.id,
        risk_amount: parseFloat(formData.risk_amount),
        profit_loss: parseFloat(formData.profit_loss),
        execution_date: formData.execution_date,
        setup: formData.setup ? formData.setup.trim() : null,
        tags: formData.tags,
        grade: formData.grade ? parseInt(formData.grade, 10) : null,
        emotion: formData.emotion ? parseInt(formData.emotion, 10) : null,
      };

      await axios.put(`${API}/trades/${editingTrade.id}?mode=${mode}`, tradeData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Trade updated successfully!");
      setOpenDialog(false);
      setEditingTrade(null);
      loadTrades();
    } catch (error) {
      toast.error("Error updating trade");
    }
  };

  const handleDelete = async (tradeId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/trades/${tradeId}?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Trade deleted successfully!");
      loadTrades();
    } catch (error) {
      toast.error("Error deleting trade");
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="mb-6">
          <Skeleton className="h-10 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="shadow-sm">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="h-6 w-24" />
                      <Skeleton className="h-6 w-32 rounded-full" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-9 w-9 rounded-md" />
                    <Skeleton className="h-9 w-9 rounded-md" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="trades-list-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Trades List</h1>
          <p className="text-muted-foreground text-sm md:text-base">
            All trades for strategy: {currentStrategy.name}
          </p>
        </div>

        {trades.length === 0 ? (
          <EmptyState
            icon={List}
            title="No trades yet"
            description="Add your first trade to start tracking your performance."
            actionLabel="Go to Dashboard"
            onAction={() => window.location.href = `/${mode}`}
            data-testid="no-trades-message"
          />
        ) : (
          <div className="space-y-4">
            {trades.map((trade, index) => (
              <motion.div
                key={trade.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`trade-card-${index}`}>
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg md:text-xl font-bold" data-testid={`trade-currency-pair-${index}`}>{trade.currency_pair}</h3>
                          <span
                            className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold ${
                              trade.profit_loss >= 0
                                ? "bg-success/10 text-success"
                                : "bg-destructive/10 text-destructive"
                            }`}
                            data-testid={`trade-profit-loss-${index}`}
                          >
                            {trade.profit_loss >= 0 ? (
                              <TrendingUp className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                            ) : (
                              <TrendingDown className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                            )}
                            {trade.profit_loss >= 0 ? "+" : ""}
                            {trade.profit_loss} {currentStrategy.currency}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>
                            <span className="font-medium">Risk:</span> {trade.risk_amount}{" "}
                            {currentStrategy.currency}
                          </p>
                          <p>
                            <span className="font-medium">Date:</span>{" "}
                            {formatExecutionDateForDisplay(trade.execution_date, "en-US")}
                          </p>
                          {trade.notes && (
                            <p className="mt-2 whitespace-pre-line" data-testid={`trade-notes-${index}`}>
                              <span className="font-medium">Notes:</span> {trade.notes}
                            </p>
                          )}
                          {(trade.setup || trade.tags?.length > 0 || trade.grade || trade.emotion) && (
                            <div className="flex flex-wrap items-center gap-2 pt-2">
                              {trade.setup && (
                                <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                                  {trade.setup}
                                </Badge>
                              )}
                              {(trade.tags || []).map((tag) => (
                                <Badge key={tag} variant="outline" className="border-destructive/40 bg-destructive/10 text-destructive">
                                  {tag}
                                </Badge>
                              ))}
                              {trade.grade && (
                                <Badge variant="outline" className="border-border bg-secondary/60">
                                  Grade {trade.grade}/5
                                </Badge>
                              )}
                              {trade.emotion && (
                                <Badge variant="outline" className="border-border bg-secondary/60">
                                  Emotion {trade.emotion}/5
                                </Badge>
                              )}
                            </div>
                          )}
                          {trade.attachments?.length > 0 && (
                            <div className="space-y-3 pt-2">
                              <p className="font-medium text-foreground">Screenshots</p>
                              <TradeAttachmentGallery attachments={trade.attachments} />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(trade)}
                          data-testid={`edit-trade-button-${index}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              data-testid={`delete-trade-button-${index}`}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid={`delete-trade-dialog-${index}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Trade?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this trade? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="rounded-lg bg-secondary/50 p-4 space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Pair:</span>
                                <span className="font-semibold">{trade.currency_pair}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Risk:</span>
                                <span className="font-semibold">{trade.risk_amount} {currentStrategy.currency}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">P/L:</span>
                                <span className={`font-semibold ${trade.profit_loss >= 0 ? "text-success" : "text-destructive"}`}>
                                  {trade.profit_loss >= 0 ? "+" : ""}{trade.profit_loss} {currentStrategy.currency}
                                </span>
                              </div>
                            </div>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="cancel-delete-trade-button">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(trade.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                data-testid="confirm-delete-trade-button"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogContent data-testid="edit-trade-dialog">
            <DialogHeader>
              <DialogTitle>Edit Trade</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-currency-pair">Currency Pair</Label>
                <Input
                  id="edit-currency-pair"
                  data-testid="edit-currency-pair-input"
                  value={formData.currency_pair}
                  onChange={(e) =>
                    setFormData({ ...formData, currency_pair: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-risk-amount">Risk ({currentStrategy.currency})</Label>
                <Input
                  id="edit-risk-amount"
                  data-testid="edit-risk-amount-input"
                  type="number"
                  step="0.01"
                  value={formData.risk_amount}
                  onChange={(e) =>
                    setFormData({ ...formData, risk_amount: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-profit-loss">Profit/Loss ({currentStrategy.currency})</Label>
                <Input
                  id="edit-profit-loss"
                  data-testid="edit-profit-loss-input"
                  type="number"
                  step="0.01"
                  value={formData.profit_loss}
                  onChange={(e) =>
                    setFormData({ ...formData, profit_loss: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-execution-date">Execution Date</Label>
                <Input
                  id="edit-execution-date"
                  data-testid="edit-execution-date-input"
                  type="date"
                  value={formData.execution_date}
                  onChange={(e) =>
                    setFormData({ ...formData, execution_date: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-setup">Setup</Label>
                <Input
                  id="edit-setup"
                  data-testid="edit-setup-input"
                  list="edit-setup-options"
                  placeholder="e.g. breakout"
                  value={formData.setup}
                  onChange={(e) => setFormData({ ...formData, setup: e.target.value })}
                />
                <datalist id="edit-setup-options">
                  {(currentStrategy?.setups || []).map((setup) => (
                    <option key={setup} value={setup} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tags">Behaviour tags</Label>
                <Input
                  id="edit-tags"
                  data-testid="edit-tags-input"
                  placeholder="fomo, moved_stop (comma separated)"
                  value={(formData.tags || []).join(", ")}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tags: e.target.value
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-grade">Execution grade</Label>
                  <Select
                    value={formData.grade || "none"}
                    onValueChange={(value) =>
                      setFormData({ ...formData, grade: value === "none" ? "" : value })
                    }
                  >
                    <SelectTrigger id="edit-grade" data-testid="edit-grade-select">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {[1, 2, 3, 4, 5].map((score) => (
                        <SelectItem key={score} value={`${score}`}>
                          {score}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-emotion">Emotion</Label>
                  <Select
                    value={formData.emotion || "none"}
                    onValueChange={(value) =>
                      setFormData({ ...formData, emotion: value === "none" ? "" : value })
                    }
                  >
                    <SelectTrigger id="edit-emotion" data-testid="edit-emotion-select">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {[1, 2, 3, 4, 5].map((score) => (
                        <SelectItem key={score} value={`${score}`}>
                          {score}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  data-testid="edit-notes-input"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={4}
                />
              </div>
              <TradeAttachmentsField
                inputId="edit-trade-attachments"
                attachments={formData.attachments}
                onChange={(attachments) => setFormData((current) => ({ ...current, attachments }))}
                onUploadingChange={setUploadingAttachments}
                disabled={!editingTrade}
              />
              <Button
                type="submit"
                className="w-full rounded-full"
                data-testid="update-trade-button"
                disabled={uploadingAttachments || !editingTrade}
              >
                {uploadingAttachments ? "Uploading screenshots..." : "Update Trade"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
